import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, Bank, Lightning, Buildings, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";

const OPTIONS = {
  paystack_dd: { label: "Paystack direct debit", desc: "Authorise a Paystack mandate on your bank account.", icon: Lightning },
  mono_dd: { label: "Mono direct debit", desc: "Authorise a Mono direct debit mandate on your bank account.", icon: Bank },
  remita: { label: "Remita salary deduction", desc: "Deducted automatically from your salary at source.", icon: Buildings },
};
const err = (e) => toast.error(formatErr(e?.response?.data?.detail) || e.message);

/** Borrower picks how they'll repay; the choice is saved on the loan and used for every instalment. */
export default function RepaymentMethodChooser({ kind, appId, methods, onActive, onRemita }) {
  const [busy, setBusy] = useState(null);
  const [pending, setPending] = useState(false);
  const ran = useRef(false);

  const confirm = async () => {
    setBusy("confirm");
    try {
      const { data } = await api.post(`/repayments/${kind}/${appId}/mandate/confirm`);
      if (data.status === "active") { toast.success("Direct debit authorised"); onActive?.(); }
      else { setPending(true); toast.info("Your bank hasn't confirmed the mandate yet — check again in a few minutes."); }
    } catch (e) { err(e); } finally { setBusy(null); }
  };

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const url = new URL(window.location.href);
    if (url.searchParams.get("mandate_ref")) {
      url.searchParams.delete("mandate_ref");
      window.history.replaceState(null, "", url.pathname + url.search);
      confirm();
    }
  }, []); // eslint-disable-line

  const choose = async (method) => {
    setBusy(method);
    try {
      const { data } = await api.post(`/repayments/${kind}/${appId}/mandate`, { method, return_url: window.location.origin + window.location.pathname });
      if (data.next === "remita_setup") return onRemita?.();
      if (data.redirect_url) return window.location.assign(data.redirect_url);
      await confirm();
    } catch (e) { err(e); } finally { setBusy(null); }
  };

  return (
    <div data-testid="repayment-method-chooser">
      <h3 className="font-display text-xl font-medium">How would you like to repay?</h3>
      <p className="text-sm text-[#4F5D4F] mt-1 mb-4">We'll collect every instalment automatically on its due date using the method you pick. You can repay early at any time with no penalty.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {methods.map((m) => {
          const o = OPTIONS[m];
          const Icon = o.icon;
          return (
            <button key={m} type="button" onClick={() => choose(m)} disabled={!!busy}
              className="text-left rounded-2xl border border-[#E5E3DB] bg-white p-5 hover:border-[#0A3622] hover:-translate-y-0.5 transition-transform disabled:opacity-60" data-testid={`repay-method-${m}`}>
              <div className="flex items-center gap-2 text-[#0A3622] font-semibold">{busy === m ? <CircleNotch size={18} className="animate-spin" /> : <Icon size={18} weight="fill" />} {o.label}</div>
              <p className="text-xs text-[#4F5D4F] mt-1.5">{o.desc}</p>
            </button>
          );
        })}
      </div>
      {pending && (
        <button onClick={confirm} disabled={!!busy} className="btn-outline mt-4 text-sm" data-testid="repay-mandate-check">
          {busy === "confirm" ? <CircleNotch size={14} className="animate-spin" /> : <ArrowClockwise size={14} />} I've authorised it — check again
        </button>
      )}
    </div>
  );
}

export function MandateBadge({ method }) {
  if (!method) return null;
  return <span className="badge badge-success inline-flex items-center gap-1" data-testid="repay-method-badge"><CheckCircle size={12} weight="fill" /> {OPTIONS[method]?.label || method}</span>;
}
